import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { CommandExecutionContext } from '../../types/commands';
import { StandardToolResult } from '../../types/standardToolResults';
import fetchService from '../../services/fetchService';
import { API_BASE_URL } from '../../api/config';

export const google_ads_query: SimpleDocCommand = {
  id: 'google_ads_query',
  logicalName: 'google_ads_query',
  version: 'v1',
  description: `Query Google Ads data using GAQL (Google Ads Query Language).

**INTEGRATION REQUIRED:** User must connect Google Ads in Settings → Integrations first.

**GAQL SYNTAX:**
SELECT field1, field2 FROM resource WHERE condition ORDER BY field LIMIT n

**COMMON RESOURCES:**
- campaign (campaigns list with status, budget, bidding)
- ad_group (ad groups within campaigns)
- ad_group_ad (individual ads)
- keyword_view (keyword performance)
- campaign_budget (budget details)
- metrics (performance data — always pair with a resource)

**COMMON METRICS FIELDS:**
metrics.clicks, metrics.impressions, metrics.cost_micros (divide by 1,000,000 for actual cost),
metrics.conversions, metrics.ctr, metrics.average_cpc, metrics.roas

**COMMON SEGMENT FIELDS:**
segments.date, segments.device, segments.ad_network_type

**DATE FILTERING:**
segments.date DURING LAST_30_DAYS
segments.date BETWEEN '2026-01-01' AND '2026-01-31'

**EXAMPLE QUERIES:**
- Campaign performance: SELECT campaign.name, campaign.status, metrics.clicks, metrics.impressions, metrics.cost_micros FROM campaign WHERE segments.date DURING LAST_30_DAYS ORDER BY metrics.cost_micros DESC LIMIT 10
- Keyword performance: SELECT ad_group_criterion.keyword.text, metrics.clicks, metrics.impressions, metrics.ctr FROM keyword_view WHERE segments.date DURING LAST_7_DAYS ORDER BY metrics.clicks DESC LIMIT 20
- Ad group status: SELECT ad_group.name, ad_group.status, campaign.name, metrics.conversions FROM ad_group WHERE campaign.status = 'ENABLED' LIMIT 50

**COST NOTE:** metrics.cost_micros is in micros (millionths). Divide by 1,000,000 for actual currency value.
`,

  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'GAQL SELECT query string',
        required: true,
        validator: (value: string) => {
          if (!value?.trim()) return 'Query cannot be empty';
          if (!value.trim().toUpperCase().startsWith('SELECT')) return 'Query must start with SELECT';
          return null;
        }
      },
      {
        name: 'customerId',
        type: 'string',
        description: 'Optional: Google Ads customer ID (overrides default). Format: 1234567890 (no dashes)',
        required: false,
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { query, customerId } = params;

    try {
      const data = await fetchService(`${API_BASE_URL}/integrations/google-ads/query`, {
        method: 'POST',
        body: JSON.stringify({ query, customerId }),
      });

      if (data.error || data.detail) {
        return {
          status: 'error',
          message: data.detail || 'Google Ads query failed',
          error: { code: 'query_failed', category: 'execution', details: data.detail },
          format: 'plaintext',
        };
      }

      return {
        status: 'success',
        message: `Retrieved ${data.row_count} rows from Google Ads.`,
        data: {
          rows: data.rows,
          row_count: data.row_count,
          query: data.query,
          customer_id: data.customer_id,
        },
        format: 'json',
      };
    } catch (error: any) {
      const detail = error.data?.detail || error.message || 'Unknown error';
      return {
        status: 'error',
        message: detail.includes('not connected') 
          ? 'Google Ads is not connected. Connect it in Settings → Integrations.'
          : `Google Ads query failed: ${detail}`,
        error: { code: 'query_error', category: error.status === 400 ? 'validation' : 'execution', details: detail },
        format: 'plaintext',
      };
    }
  }
};
